import React, { useState, useEffect } from 'react';
import { useUser } from '../provider/UserProvider';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Divider,
  FormControlLabel,
  Checkbox,
  Tab,
  Tabs,
  useTheme,
  Chip,
  Avatar,
  tableCellClasses,
  styled,
  InputAdornment,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormGroup,
  FormLabel,
  Switch,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  SupervisorAccount,
  DirectionsCar as DriverIcon,
  Engineering as MechanicIcon,
  Person as UserIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security as SecurityIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '../utlis/axiosInstance';

// API endpoint
const PERMISSION_API = '/gpstracker/permissionhandler/';

// Interface for API response
interface ApiResponse {
  responseData: any;
  status: boolean;
  message: string;
}

// Interface for API permission
interface ApiPermission {
  id: number;
  name: string;
}

// Transformed permission interface for our app
interface Permission {
  id: number;
  name: string;
  description: string;
  type: string;
  permissions: string[];
  users: number;
  lastModified: string;
}

const permissionGroups = [
  {
    name: 'Dashboard Permissions',
    permissions: [
      { id: 'view-dashboard', label: 'View Dashboard' },
      { id: 'edit-dashboard', label: 'Edit Dashboard' },
      { id: 'create-dashboard', label: 'Create Custom Dashboard' },
    ]
  },
  {
    name: 'Vehicle Management',
    permissions: [
      { id: 'view-vehicles', label: 'View Vehicles' },
      { id: 'add-vehicles', label: 'Add Vehicles' },
      { id: 'edit-vehicles', label: 'Edit Vehicles' },
      { id: 'delete-vehicles', label: 'Delete Vehicles' },
      { id: 'track-vehicles', label: 'Track Vehicles' },
      { id: 'manage-maintenance', label: 'Manage Maintenance' },
      { id: 'assign-vehicles', label: 'Assign Vehicles' }
    ]
  },
  {
    name: 'Driver Management',
    permissions: [
      { id: 'view-drivers', label: 'View Drivers' },
      { id: 'add-drivers', label: 'Add Drivers' },
      { id: 'edit-drivers', label: 'Edit Drivers' },
      { id: 'delete-drivers', label: 'Delete Drivers' },
      { id: 'assign-tasks', label: 'Assign Tasks' },
      { id: 'track-drivers', label: 'Track Drivers' }
    ]
  },
  {
    name: 'User Management',
    permissions: [
      { id: 'view-users', label: 'View Users' },
      { id: 'add-users', label: 'Add Users' },
      { id: 'edit-users', label: 'Edit Users' },
      { id: 'delete-users', label: 'Delete Users' },
      { id: 'manage-roles', label: 'Manage Roles' }
    ]
  },
  {
    name: 'Reports',
    permissions: [
      { id: 'view-reports', label: 'View Reports' },
      { id: 'create-reports', label: 'Create Reports' },
      { id: 'export-reports', label: 'Export Reports' },
      { id: 'schedule-reports', label: 'Schedule Reports' },
      { id: 'share-reports', label: 'Share Reports' }
    ]
  },
  {
    name: 'Geofencing',
    permissions: [
      { id: 'view-geofences', label: 'View Geofences' },
      { id: 'create-geofences', label: 'Create Geofences' },
      { id: 'edit-geofences', label: 'Edit Geofences' },
      { id: 'delete-geofences', label: 'Delete Geofences' },
      { id: 'assign-geofences', label: 'Assign Geofences' }
    ]
  },
  {
    name: 'Video Recording',
    permissions: [
      { id: 'view-recordings', label: 'View Recordings' },
      { id: 'manage-cameras', label: 'Manage Cameras' },
      { id: 'download-recordings', label: 'Download Recordings' },
      { id: 'delete-recordings', label: 'Delete Recordings' }
    ]
  },
  {
    name: 'Notifications',
    permissions: [
      { id: 'view-notifications', label: 'View Notifications' },
      { id: 'manage-templates', label: 'Manage Templates' },
      { id: 'create-alerts', label: 'Create Alerts' },
      { id: 'edit-alerts', label: 'Edit Alerts' }
    ]
  },
  {
    name: 'Settings',
    permissions: [
      { id: 'view-settings', label: 'View Settings' },
      { id: 'edit-settings', label: 'Edit Settings' },
      { id: 'system-config', label: 'System Configuration' },
      { id: 'manage-billing', label: 'Manage Billing' },
      { id: 'manage-subscription', label: 'Manage Subscription' }
    ]
  }
];

// New permission form data interface
interface PermissionFormData {
  name: string;
  description: string;
  type: string;
  permissions: string[];
}

// Get all available permissions as a flat array
const getAllPermissions = () => {
  return permissionGroups.flatMap(group => group.permissions.map(p => p.id));
};

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 600,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '10',
  },
}));

const PERMISSION_TYPES = [
  { value: 'custom', label: 'Custom' }
];

const PermissionManagement: React.FC = () => {
  const theme = useTheme();
  const { permissions: userPermissions, user } = useUser();

  // Check if user is admin
  const isAdmin = user?.user?.administrator;

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPermission, setCurrentPermission] = useState<Permission | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New form data state
  const [formData, setFormData] = useState<PermissionFormData>({
    name: '',
    description: '',
    type: 'custom',
    permissions: []
  });

  // Fetch permissions from API - only for admin users
  useEffect(() => {
    if (isAdmin) {
      fetchPermissions();
    }
  }, [isAdmin]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<ApiResponse>(PERMISSION_API, {
        params: {
          limit: 10000,
          offset: 0,
        },
      });
      const data = response.data;

      if (data.status && data.responseData) {
        // Transform API permissions to match our application's Permission interface
        const apiPermissions = data.responseData as ApiPermission[];
        const transformedPermissions: Permission[] = apiPermissions.map(apiPermission => ({
          id: apiPermission.id,
          name: apiPermission.name,
          description: '',  // API doesn't provide this yet
          type: 'custom',
          permissions: [],  // API doesn't provide this yet
          users: 0,         // API doesn't provide this yet
          lastModified: new Date().toISOString().split('T')[0]
        }));

        setPermissions(transformedPermissions);
      } else {
        setError('Failed to fetch permissions: ' + data.message);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to fetch permissions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPermissions = permissions.filter(permission =>
    permission.name.toLowerCase().includes(search.toLowerCase()) ||
    permission.description.toLowerCase().includes(search.toLowerCase())
  );

  // Initialize form when opening dialog
  useEffect(() => {
    if (open) {
      if (editMode && currentPermission) {
        setFormData({
          name: currentPermission.name,
          description: currentPermission.description,
          type: currentPermission.type,
          permissions: currentPermission.permissions
        });

        // Update select all checkboxes based on current permissions
        updateSelectAllStatus(currentPermission.permissions);
      } else {
        // Reset form for new permission
        setFormData({
          name: '',
          description: '',
          type: 'custom',
          permissions: []
        });
        setSelectAllChecked({});
      }
    }
  }, [open, editMode, currentPermission]);

  // Update select all status when permissions change
  const updateSelectAllStatus = (permissions: string[]) => {
    const newSelectAllStatus: Record<string, boolean> = {};

    permissionGroups.forEach(group => {
      const groupPermissionIds = group.permissions.map(p => p.id);
      const allChecked = groupPermissionIds.every(id => permissions.includes(id));
      newSelectAllStatus[group.name] = allChecked;
    });

    setSelectAllChecked(newSelectAllStatus);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAddPermission = () => {
    setCurrentPermission(null);
    setEditMode(false);
    setTabValue(0);
    // Start with permissions tab if no permissions exist
    if (permissions.length === 0) {
      setTabValue(1);
    }
    setOpen(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setCurrentPermission(permission);
    setEditMode(true);
    setTabValue(0);
    setOpen(true);
  };

  const handleDeletePermission = (permission: Permission) => {
    setCurrentPermission(permission);
    setDeleteDialogOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteDialogOpen(false);
    setFormData({
      name: '',
      description: '',
      type: 'custom',
      permissions: []
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle text input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle permission type change
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle permission checkbox change
  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    setFormData(prev => {
      let updatedPermissions;
      if (checked) {
        updatedPermissions = [...prev.permissions, permissionId];
      } else {
        updatedPermissions = prev.permissions.filter(id => id !== permissionId);
      }

      // Update select all status
      updateSelectAllStatus(updatedPermissions);

      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  // Handle select all for a permission group
  const handleSelectAllGroup = (groupName: string, checked: boolean) => {
    const group = permissionGroups.find(g => g.name === groupName);
    if (!group) return;

    const groupPermissionIds = group.permissions.map(p => p.id);

    setFormData(prev => {
      let updatedPermissions;
      if (checked) {
        // Add all permissions from group that aren't already included
        const newPermissions = groupPermissionIds.filter(id => !prev.permissions.includes(id));
        updatedPermissions = [...prev.permissions, ...newPermissions];
      } else {
        // Remove all permissions from this group
        updatedPermissions = prev.permissions.filter(id => !groupPermissionIds.includes(id));
      }

      // Update select all checkboxes
      setSelectAllChecked({
        ...selectAllChecked,
        [groupName]: checked
      });

      return {
        ...prev,
        permissions: updatedPermissions
      };
    });
  };

  // Handle save permission
  const handleSavePermission = async () => {
    if (formData.name.trim() === '') {
      // Validation could be improved
      console.error('Permission name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editMode && currentPermission) {
        // Update existing permission - API integration will be added here
        // For now, just update the local state
        setPermissions(prevPermissions =>
          prevPermissions.map(permission =>
            permission.id === currentPermission.id
              ? {
                ...permission,
                name: formData.name,
                description: formData.description,
                type: 'custom', // Always use custom type
                permissions: formData.permissions,
                lastModified: new Date().toISOString().split('T')[0]
              }
              : permission
          )
        );
      } else {
        // Create new permission
        const payload = {
          name: formData.name
        };

        const response = await axiosInstance.post<ApiResponse>(PERMISSION_API, payload);

        const data = response.data;

        if (data.status) {
          // After successful API call, fetch the updated permissions
          await fetchPermissions();
        } else {
          setError('Failed to create permission: ' + data.message);
        }
      }

      handleClose();
    } catch (err) {
      console.error('Error saving permission:', err);
      setError('Failed to save permission. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!currentPermission) return;

    setLoading(true);
    setError(null);

    try {
      // API integration for delete will be added here
      // For now, just update the local state
      setPermissions(prevPermissions => prevPermissions.filter(permission => permission.id !== currentPermission.id));
      handleClose();
    } catch (err) {
      console.error('Error deleting permission:', err);
      setError('Failed to delete permission. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getPermissionIcon = (type: string) => {
    // Always return the security icon for custom permissions
    return <SecurityIcon />;
  };

  const getPermissionColor = (type: string) => {
    // Always use a consistent color for custom permissions
    return theme.palette.primary.main;
  };

  // Show loading while checking admin status
  if (isAdmin === undefined) {
    return (
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Checking permissions...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show access denied for non-admin users
  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <SecurityIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="error.main">
            Access Denied
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Permission Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You don't have permission to access Permission Management. This feature is only available for administrators.
          </Typography>
          <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
            Permission Management is restricted to administrators only. Please contact your system administrator if you need access to this feature.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold" color="text.primary">
          Permission Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPermission}
          sx={{
            bgcolor: "var(--primary-color)",
            "&:hover": {
              bgcolor: "var(--primary-light)",
            },
          }}
        >
          Create New Permission
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !open && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      {!loading && permissions.length === 0 ? (
        <Paper sx={{ p: 4, mb: 3, borderRadius: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <SecurityIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>No Permissions Defined</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Get started by creating your first custom permission. You can define specific permissions for each role.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPermission}
              size="large"
              sx={{
                bgcolor: "var(--primary-color)",
                "&:hover": {
                  bgcolor: "var(--primary-light)",
                },
              }}
            >
              Create Your First Permission
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              placeholder="Search permissions..."
              size="small"
              value={search}
              onChange={handleSearch}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button startIcon={<FilterIcon />} variant="outlined">
              Filter
            </Button>
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Permission Name</StyledTableCell>
                  <StyledTableCell>Description</StyledTableCell>
                  <StyledTableCell align="center">Users</StyledTableCell>
                  <StyledTableCell>Last Modified</StyledTableCell>
                  <StyledTableCell>Permissions</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && filteredPermissions.map((permission: Permission) => (
                  <StyledTableRow key={permission.id}>
                    <StyledTableCell component="th" scope="row">
                      <Box display="flex" alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: `${getPermissionColor(permission.type)}20`,
                            color: getPermissionColor(permission.type),
                            mr: 1.5
                          }}
                        >
                          {getPermissionIcon(permission.type)}
                        </Avatar>
                        <Typography variant="subtitle2">
                          {permission.name}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{permission.description}</StyledTableCell>
                    <StyledTableCell align="center">{permission.users}</StyledTableCell>
                    <StyledTableCell>{permission.lastModified}</StyledTableCell>
                    <StyledTableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {permission.permissions.slice(0, 3).map((permission, index) => (
                          <Chip
                            key={index}
                            label={permission.replace(/-/g, ' ')}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ))}
                        {permission.permissions.length > 3 && (
                          <Chip
                            label={`+${permission.permissions.length - 3} more`}
                            size="small"
                            color="default"
                          />
                        )}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditPermission(permission)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeletePermission(permission)} disabled={permission.users > 0}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                {!loading && filteredPermissions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No permissions found. Create a new permission to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Add/Edit Permission Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? `Edit Permission: ${currentPermission?.name}` : 'Create New Permission'}
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Permission Details" />
            <Tab label="Permissions" />
          </Tabs>

          {tabValue === 0 && (
            <Box sx={{ pt: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label="Permission Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ mb: 3 }}
              />
              <TextField
                margin="dense"
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                sx={{ mb: 3 }}
              />
              <Typography variant="body2" color="text.secondary">
                Define a descriptive name and purpose for this permission. In the next tab, you'll be able to set specific permissions.
              </Typography>
              <Box sx={{ mt: 2, bgcolor: 'info.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  Tip: After creating your permission, you can assign it to users in the User Management section.
                </Typography>
              </Box>
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ pt: 2, height: 400, overflow: 'auto' }}>
              {permissionGroups.map((group, groupIndex) => (
                <Box key={groupIndex} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {group.name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selectAllChecked[group.name]}
                          onChange={(e) => handleSelectAllGroup(group.name, e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Select All"
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    {group.permissions.map((permission, permIndex) => (
                      <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }} key={permIndex}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.permissions.includes(permission.id)}
                              onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2">{permission.label}</Typography>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSavePermission}
            variant="contained"
            disabled={loading || !formData.name.trim()}
            sx={{
              bgcolor: "var(--primary-color)",
              "&:hover": {
                bgcolor: "var(--primary-light)",
              },
            }}
          >
            {editMode ? 'Update Permission' : 'Create Permission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleClose}
      >
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          <DialogContentText>
            Are you sure you want to delete the permission "{currentPermission?.name}"? This action cannot be undone.
          </DialogContentText>
          {currentPermission && currentPermission.users > 0 && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              This permission is assigned to {currentPermission.users} users. You must reassign users before deleting this permission.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading || !!(currentPermission && currentPermission.users > 0)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PermissionManagement; 